/*
Based on Material Dashboard 2 React by Creative Tim (https://www.creative-tim.com)
Licensed under the MIT License (see LICENSE)

Original Copyright (c) 2013-2021 Creative Tim
Modifications (c) 2025 CodeWeave Solutions (Pty) Ltd
*/

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Skeleton from "@mui/material/Skeleton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// Custom components
import ContractTemplateUploadModal from "components/ContractTemplateUploadModal";
import CreateTemplateBetaModal from "components/CreateTemplateBetaModal";
import TemplateViewModal from "components/TemplateViewModal";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";

// Data
import contractTemplatesData from "./data/contractTemplatesData";
import templateFoldersData from "./data/templateFoldersData";

const Template = ({ name, description }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDBox ml={2} lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {name}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  </MDBox>
);

const TableSkeleton = () => (
  <MDBox>
    {/* Table header skeleton */}
    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Skeleton variant="text" width={200} height={32} />
      <Skeleton variant="text" width={100} height={24} />
    </MDBox>
    
    {/* Table rows skeleton */}
    {[1, 2, 3, 4, 5].map((index) => (
      <MDBox key={index} display="flex" alignItems="center" py={2} borderBottom="1px solid #eee">
        <MDBox flex={1} display="flex" alignItems="center">
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <MDBox flex={1}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={16} />
          </MDBox>
        </MDBox>
        <MDBox width={120} textAlign="center">
          <Skeleton variant="text" width={80} height={20} />
        </MDBox>
        <MDBox width={150} display="flex" justifyContent="center" gap={1}>
          <Skeleton variant="rectangular" width={60} height={32} />
          <Skeleton variant="rectangular" width={60} height={32} />
        </MDBox>
      </MDBox>
    ))}
  </MDBox>
);

const FolderSkeleton = () => (
  <MDBox p={3}>
    {[1, 2, 3, 4, 5, 6].map((index) => (
      <MDBox key={index} display="flex" alignItems="center" justifyContent="space-between" p={2} mb={1}>
        <MDBox display="flex" alignItems="center">
          <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
          <Skeleton variant="text" width={120} height={24} />
        </MDBox>
        <Skeleton variant="circular" width={24} height={24} />
      </MDBox>
    ))}
  </MDBox>
);

function ContractTemplates() {
  const [selectedFolder, setSelectedFolder] = useState("all");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreateBetaModalOpen, setIsCreateBetaModalOpen] = useState(false);
  const [isTemplateViewModalOpen, setIsTemplateViewModalOpen] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState(null);
  const [selectedTemplateForView, setSelectedTemplateForView] = useState(null);
  const [templateHtml, setTemplateHtml] = useState("");
  const [isHtmlLoading, setIsHtmlLoading] = useState(false);

  // Get folders data and update counts based on API data
  const { folders: baseFolders } = templateFoldersData();
  
  // Update folder counts based on actual API data
  const folders = baseFolders.map(folder => {
    if (folder.id === "all") {
      return { ...folder, count: templates.length };
    }
    
    // Map folder names to folder IDs for counting
    const folderIdMap = {
      "investments": 1,
      "medical-aid-gap": 2, 
      "misc": 3,
      "onboarding": 4,
      "risk": 5
    };
    
    const folderId = folderIdMap[folder.id];
    const count = templates.filter(template => template.folder_id === folderId).length;
    
    return { ...folder, count };
  });

  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetchWithAuth('/api/templates/branch-templates', {
          method: 'GET',
        });

        if (!response || !response.ok) {
          throw new Error(`HTTP error! status: ${response ? response.status : 'unknown'}`);
        }

        const data = await response.json();
        
        // Sort templates by folder_id
        const sortedTemplates = data.sort((a, b) => a.folder_id - b.folder_id);
        setTemplates(sortedTemplates);
        
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleTemplateClick = async (template) => {
    try {
      setSelectedTemplateForView(template);
      setIsTemplateViewModalOpen(true);
      setTemplateHtml("");
      setIsHtmlLoading(true);
      // Always use a valid dummy UUID for client_id
      const body = {
        template_json: template.json, // must be under 'template_json' key
        client_id: "7d3cbd6d-868a-4291-97b3-86da0087181b"
      };
      const response = await fetchWithAuth('/api/templates/render-template-html', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (!response || !response.ok) {
        throw new Error(`Failed to render template: ${response ? response.status : 'unknown'}`);
      }
      const data = await response.json();
      setTemplateHtml(data.html);
    } catch (err) {
      setTemplateHtml(`<div style='color:red'>Error: ${err.message}</div>`);
    } finally {
      setIsHtmlLoading(false);
    }
  };

  // Get table data using the updated contractTemplatesData function
  const { columns, rows } = contractTemplatesData(templates, selectedFolder, searchQuery, handleTemplateClick);

  const handleFolderSelect = (folderId) => {
    setSelectedFolder(folderId);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCreate = () => {
    setIsUploadModalOpen(true);
  };



  const handleClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleUpload = (data) => {
    console.log("Uploaded file:", data.file);
    // TODO: Handle file upload
    setIsUploadModalOpen(false);
  };

  const handleOpenCreateBetaModal = () => setIsCreateBetaModalOpen(true);
  const handleCloseCreateBetaModal = () => setIsCreateBetaModalOpen(false);
  const handleSaveBetaTemplate = (template) => {
    // TODO: Save the beta template to backend or state
    console.log("New beta template:", template);
    setIsCreateBetaModalOpen(false);
  };

  const handleCloseTemplateViewModal = () => {
    setIsTemplateViewModalOpen(false);
    setSelectedTemplateForView(null);
  };



  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6} sx={{ display: 'flex', flexWrap: 'nowrap' }}>
          {/* Left Side - Template Folders */}
          <Grid item xs={12} sm={4} md={3} lg={2} sx={{ minWidth: '250px' }}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Template Folders
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                {loading ? (
                  <FolderSkeleton />
                ) : (
                  folders.map((folder) => (
                    <MDBox
                      key={folder.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p={2}
                      mb={1}
                      borderRadius="lg"
                      sx={{
                        cursor: "pointer",
                        backgroundColor: selectedFolder === folder.id ? "info.main" : "inherit",
                        color: selectedFolder === folder.id ? "white" : "text.primary",
                        "&:hover": {
                          backgroundColor: selectedFolder === folder.id ? "info.dark" : "grey.100",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                      onClick={() => handleFolderSelect(folder.id)}
                    >
                      <MDBox display="flex" alignItems="center">
                        <Icon sx={{ mr: 1, fontSize: "1.2rem" }}>
                          {folder.icon || (selectedFolder === folder.id ? "folder_open" : "folder")}
                        </Icon>
                        <MDTypography variant="button" fontWeight="medium">
                          {folder.name}
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                        sx={{
                          backgroundColor: selectedFolder === folder.id ? "white" : "grey.300",
                          color: selectedFolder === folder.id ? "info.main" : "text.secondary",
                          width: "24px",
                          height: "24px",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        {folder.count}
                      </MDBox>
                    </MDBox>
                  ))
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Right Side - Contract Templates Table */}
          <Grid item xs={12} sm={8} md={9} lg={10} sx={{ flex: 1 }}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Contract Templates
                  </MDTypography>
                  <MDTypography variant="caption" color="white" opacity={0.8}>
                    {selectedFolder === "all" 
                      ? "All Templates" 
                      : folders.find(f => f.id === selectedFolder)?.name}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={handleOpenCreateBetaModal}
                    startIcon={<Icon>auto_awesome</Icon>}
                  >
                    Create New Template (Beta)
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                {/* Search Bar */}
                <MDBox mb={3}>
                  <MDBox
                    display="flex"
                    alignItems="center"
                    sx={{
                      position: "relative",
                      width: "100%",
                    }}
                  >
                    <Icon
                      sx={{
                        position: "absolute",
                        left: "12px",
                        color: "#999",
                        fontSize: "20px",
                        zIndex: 1,
                      }}
                    >
                      search
                    </Icon>
                    <MDBox
                      component="input"
                      type="text"
                      placeholder="Search templates..."
                      value={searchQuery}
                      onChange={handleSearch}
                      sx={{
                        width: "100%",
                        padding: "12px 16px 12px 44px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s ease",
                        "&:focus": {
                          borderColor: "#1976d2",
                          boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                        },
                        "&::placeholder": {
                          color: "#999",
                        },
                      }}
                    />
                  </MDBox>
                </MDBox>
                
                {loading ? (
                  <TableSkeleton />
                ) : error ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <MDTypography variant="body2" color="error">
                      Error loading templates: {error}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <ContractTemplateUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleClose}
        onSave={handleUpload}
      />

      <CreateTemplateBetaModal
        isOpen={isCreateBetaModalOpen}
        onClose={handleCloseCreateBetaModal}
        onSave={handleSaveBetaTemplate}
        initialTemplate={selectedTemplateForPreview}
      />

      <TemplateViewModal
        isOpen={isTemplateViewModalOpen}
        onClose={handleCloseTemplateViewModal}
        template={selectedTemplateForView}
      />

    </DashboardLayout>
  );
}

export default ContractTemplates; 